/**
 * Plain LRC files use real Unicode (e.g. Hangul). Some editors or hosts store the same
 * lyrics as RTF, which encodes characters as decimal \uN escapes (often after \uc0).
 * Decode those so [mm:ss.xx] lines and karaoke highlighting work.
 */
export function normalizeLyricsText(raw: string): string {
  if (!raw) {
    return raw;
  }

  let s = String(raw).trim();

  // Some URLs return JSON payloads or quoted strings instead of raw lyrics content.
  if ((s.startsWith("{") && s.endsWith("}")) || (s.startsWith("[") && s.endsWith("]"))) {
    try {
      const parsed = JSON.parse(s) as
        | string
        | { lyrics?: unknown; lyricsText?: unknown; syncedLyrics?: unknown; plainLyrics?: unknown };
      if (typeof parsed === "string") {
        s = parsed;
      } else if (parsed && typeof parsed === "object") {
        const candidate =
          parsed.syncedLyrics ?? parsed.lyrics ?? parsed.lyricsText ?? parsed.plainLyrics;
        if (typeof candidate === "string") {
          s = candidate;
        }
      }
    } catch {
      // keep original string
    }
  }

  // If the response itself is a JSON-escaped string, unwrap a few times.
  for (let i = 0; i < 3; i += 1) {
    const trimmed = s.trim();
    if (!(trimmed.startsWith('"') && trimmed.endsWith('"'))) break;
    try {
      const parsed = JSON.parse(trimmed) as unknown;
      if (typeof parsed !== "string") break;
      s = parsed;
    } catch {
      break;
    }
  }

  if (!s.includes("\\")) {
    return s;
  }

  if (!/\\u-?\d+/.test(s)) {
    return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
  }

  s = s.replace(/\\par\b/gi, "\n").replace(/\\line\b/gi, "\n");

  s = s.replace(/\\uc\d+\s?/gi, "");

  s = s.replace(/\\u(-?\d+)\s?/g, (_match, d: string) => {
    const n = parseInt(d, 10);
    if (Number.isNaN(n)) return "";
    const codePoint = n < 0 ? 65536 + n : n;
    if (codePoint < 0 || codePoint > 0x10ffff) return "";
    return String.fromCodePoint(codePoint);
  });

  s = s.replace(/\\'([0-9a-f]{2})/gi, (_m, hex: string) => {
    const code = parseInt(hex, 16);
    if (Number.isNaN(code)) return "";
    return String.fromCharCode(code);
  });

  s = s.replace(/\\[a-z]+(-?\d+)?\s?/gi, "");

  s = s.replace(/[{}]/g, "");

  return s.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim();
}
